class ThumbnailScrollArrows extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const scroller = this.#scroller;

    this.#previousButton?.addEventListener('click', () => this.#scroll('previous'), { signal });
    this.#nextButton?.addEventListener('click', () => this.#scroll('next'), { signal });
    scroller?.addEventListener('scroll', this.#queueUpdate, { signal, passive: true });

    if (scroller) {
      this.#resizeObserver.observe(scroller);
      this.#mutationObserver.observe(scroller, { childList: true, subtree: true });
    }

    if (this.#thumbnailList) {
      this.#resizeObserver.observe(this.#thumbnailList);
    }

    this.#queueUpdate();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  get #isVertical() {
    return this.dataset.scrollMode === 'vertical';
  }

  get #scroller() {
    return this.querySelector('scroll-hint.slideshow-controls__thumbnails-container');
  }

  get #thumbnailList() {
    return this.querySelector('.slideshow-controls__thumbnails');
  }

  get #previousButton() {
    return this.querySelector('[data-thumbnail-scroll-direction="previous"]');
  }

  get #nextButton() {
    return this.querySelector('[data-thumbnail-scroll-direction="next"]');
  }

  /**
   * @param {'previous' | 'next'} direction
   */
  #scroll(direction) {
    const scroller = this.#scroller;
    if (!scroller || !this.#isVertical) return;

    const distance = this.#scrollDistance;
    scroller.scrollBy({
      top: direction === 'previous' ? -distance : distance,
      behavior: 'smooth',
    });
  }

  get #scrollDistance() {
    const thumbnail = this.querySelector('.slideshow-controls__thumbnail');
    const thumbnailHeight = thumbnail?.getBoundingClientRect().height || this.#thumbnailWidthFallback;
    const rowGap = this.#thumbnailRowGap;

    return Math.max(thumbnailHeight + rowGap, 1);
  }

  get #thumbnailWidthFallback() {
    const width = Number.parseFloat(getComputedStyle(this).getPropertyValue('--thumbnail-width'));

    return Number.isFinite(width) ? width : 44;
  }

  get #thumbnailRowGap() {
    const thumbnailList = this.#thumbnailList;
    if (!thumbnailList) return 0;

    const styles = getComputedStyle(thumbnailList);
    const rowGap = Number.parseFloat(styles.rowGap || styles.gap);

    return Number.isFinite(rowGap) ? rowGap : 0;
  }

  #queueUpdate = () => {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #update() {
    const scroller = this.#scroller;
    const previousButton = this.#previousButton;
    const nextButton = this.#nextButton;

    if (!scroller || !previousButton || !nextButton || !this.#isVertical) {
      this.#setButtonState(false, false);
      this.toggleAttribute('data-has-overflow', false);
      return;
    }

    const maxScrollTop = scroller.scrollHeight - scroller.clientHeight;
    const hasOverflow = maxScrollTop > 1;
    const canScrollPrevious = hasOverflow && scroller.scrollTop > 1;
    const canScrollNext = hasOverflow && scroller.scrollTop < maxScrollTop - 1;

    this.#setButtonState(canScrollPrevious, canScrollNext);
    this.toggleAttribute('data-has-overflow', hasOverflow);
  }

  /**
   * @param {boolean} canScrollPrevious
   * @param {boolean} canScrollNext
   */
  #setButtonState(canScrollPrevious, canScrollNext) {
    if (this.#previousButton) this.#previousButton.hidden = !canScrollPrevious;
    if (this.#nextButton) this.#nextButton.hidden = !canScrollNext;
  }

  #resizeObserver = new ResizeObserver(this.#queueUpdate);

  #mutationObserver = new MutationObserver(this.#queueUpdate);
}

if (!customElements.get('thumbnail-scroll-arrows')) {
  customElements.define('thumbnail-scroll-arrows', ThumbnailScrollArrows);
}
